<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';
import UserAvatar from '@/components/UserAvatar.vue';
import RoleBadge from '@/components/RoleBadge.vue';
import type { MemberUser } from '@/types/api';
import { fetchMembers } from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';

const router = useRouter();
const { token } = useAuth();

const loading = ref(true);
const members = ref<MemberUser[]>([]);

onMounted(async () => {
  if (!token.value) return;
  try {
    members.value = (await fetchMembers(token.value)).members;
  } finally {
    loading.value = false;
  }
});

function openProfile(id: number) {
  router.push({ name: 'user-profile', params: { id } });
}
</script>

<template>
  <div class="page">
    <section class="page__hero">
      <h1>Пользователи</h1>
      <p>Кто как-то оказался в Убежище. Жми на карточку.</p>
    </section>

    <ProgressSpinner v-if="loading" />

    <div v-else class="members-grid">
      <Card
        v-for="member in members"
        :key="member.id"
        class="member-card"
        @click="openProfile(member.id)"
      >
        <template #content>
          <div class="member-card__body">
            <UserAvatar :nickname="member.nickname" :avatar="member.avatar" size="xlarge" />
            <div class="member-card__info">
              <strong>{{ member.nickname }}</strong>
              <RoleBadge :label="member.roleLabel" :color="member.roleBadgeColor" />
              <p v-if="member.about" class="member-card__about">{{ member.about }}</p>
              <small v-if="member.avatarLikesCount > 0">❤️ {{ member.avatarLikesCount }}</small>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.page__hero h1 {
  margin: 0 0 0.35rem;
  color: #831843;
}

.page__hero p {
  margin: 0;
  color: #9d174d;
}

.members-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.member-card {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.member-card:hover {
  transform: translateY(-3px);
}

.member-card__body {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.member-card__info {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  align-items: flex-start;
}

.member-card__info strong {
  color: #831843;
  font-size: 1.1rem;
}

.member-card__about {
  margin: 0;
  color: #9d174d;
  font-size: 0.92rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.member-card__info small {
  color: #db2777;
}
</style>
